package com.tovtalk.data.mapper

import com.tovtalk.data.network.BandwidthEstimator
import com.tovtalk.data.remote.dto.ActivityNotificationDto
import com.tovtalk.data.remote.dto.AdminNotificationDto
import com.tovtalk.data.remote.dto.UnifiedNotificationItemDto
import com.tovtalk.domain.entity.ActivityType
import com.tovtalk.domain.entity.Notification
import com.tovtalk.domain.entity.User
import kotlin.time.Instant

/**
 * Notification Mapper - DTO to Entity
 */
class NotificationMapper(private val bandwidth: BandwidthEstimator) {

    fun mapUnifiedToEntity(dto: UnifiedNotificationItemDto): Notification = when (dto.type) {
        "admin" -> Notification.Admin(
            id = dto.id,
            title = dto.title ?: "",
            message = dto.description ?: "",
            imageUrl = dto.image,
            actionUrl = null,
            createdAt = parseInstant(dto.createdAt),
            isRead = dto.isRead
        )
        else -> Notification.Activity(
            id = dto.id,
            type = mapActivityType(dto.notificationType ?: "like_post"),
            fromUser = dto.fromUser?.let { UserMapper.mapToEntity(it) } ?: unknownUserPlaceholder(dto.fromUserId),
            post = dto.post?.let { it.toPost(bandwidth) },
            comment = dto.comment?.let { CommentMapper.mapToEntity(it) },
            createdAt = parseInstant(dto.createdAt),
            isRead = dto.isRead
        )
    }

    fun mapActivityToEntity(dto: ActivityNotificationDto): Notification.Activity {
        return Notification.Activity(
            id = dto.id,
            type = mapActivityType(dto.type),
            fromUser = UserMapper.mapToEntity(dto.fromUser),
            post = dto.post?.let { it.toPost(bandwidth) },
            comment = dto.comment?.let { CommentMapper.mapToEntity(it) },
            createdAt = parseInstant(dto.createdAt),
            isRead = dto.isRead
        )
    }

    fun mapAdminToEntity(dto: AdminNotificationDto): Notification.Admin {
        return Notification.Admin(
            id = dto.id,
            title = dto.title,
            message = dto.message,
            imageUrl = dto.imageUrl,
            actionUrl = dto.actionUrl,
            createdAt = parseInstant(dto.createdAt),
            isRead = dto.isRead == 1
        )
    }

    private fun mapActivityType(type: String): ActivityType {
        return when (type.lowercase()) {
            "like_post" -> ActivityType.LIKE_POST
            "like_comment" -> ActivityType.LIKE_COMMENT
            "comment_post" -> ActivityType.COMMENT_POST
            "reply_comment" -> ActivityType.REPLY_COMMENT
            "mention_post" -> ActivityType.MENTION_POST
            "mention_comment" -> ActivityType.MENTION_COMMENT
            "follow_user" -> ActivityType.FOLLOW_USER
            "new_message" -> ActivityType.NEW_MESSAGE
            else -> ActivityType.LIKE_POST
        }
    }

    private fun unknownUserPlaceholder(userId: Int?): User {
        val instant = Instant.fromEpochMilliseconds(0)
        return User(
            id = userId ?: 0,
            firebaseUid = "",
            identity = "",
            fullname = "Unknown",
            username = "unknown",
            email = null,
            profilePhoto = null,
            bio = null,
            createdAt = instant,
            updatedAt = instant
        )
    }
}
